import React, { useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import * as VideoActions from '../../stores/actions/video';
import * as VideoStreams from '../../utils/video/video-streams';
import SelectSmall from '../general-components/select-small';
import AudioMeter from '../general-components/audio-meter';
import Colors from '../../utils/colors';
import Icon, { ICONS, COLORS } from '../general-components/icon';

const audioTest = require('../../audio/brandlive.mp3');

const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

export default function AudioVideoCheck() {
	const videoDevices = useSelector((s: GS) => s.VideoReducer.videoDevices);
	const audioDevices = useSelector((s: GS) => s.VideoReducer.audioDevices);
	const audioOutputs = useSelector((s: GS) => s.VideoReducer.audioOutputs);
	const selectedVideo = useSelector((s: GS) => s.VideoReducer.selectedVideo);
	const selectedAudio = useSelector((s: GS) => s.VideoReducer.selectedAudio);
	const selectedOutput = useSelector((s: GS) => s.VideoReducer.selectedOutput);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const dispatch = useDispatch();

	const audioEl = useRef<HTMLAudioElement | null>(null);

	async function selectMic(val: string) {
		const selectedAud = audioDevices.find((device: MediaDeviceInfo) => device.deviceId === val);
		if (selectedAud) {
			dispatch(VideoActions.setSelectedAudio(selectedAud));
		}

		if (selectedVideo) {
			dispatch(VideoActions.destroyMainVideoStream());
			const str = await VideoStreams.getCameraStream(selectedVideo.deviceId, val);
			dispatch(VideoActions.setMainVideoStream(str));
		}
	}

	async function selectSpeaker(val: string) {
		const selectedOut = audioOutputs.find((device: MediaDeviceInfo) => device.deviceId === val);
		if (selectedOut) {
			dispatch(VideoActions.setSelectedAudioOutput(selectedOut));
		}

		const el = audioEl.current as any;
		el?.setSinkId(val);
	}

	async function selectCamera(val: string) {
		const selectedVid = videoDevices.find((device: MediaDeviceInfo) => device.deviceId === val);
		if (selectedVid) {
			dispatch(VideoActions.setSelectedVideo(selectedVid));
		}

		if (selectedAudio) {
			dispatch(VideoActions.destroyMainVideoStream());
			const str = await VideoStreams.getCameraStream(val, selectedAudio.deviceId);
			dispatch(VideoActions.setMainVideoStream(str));
		}
	}

	function testAudio() {
		audioEl.current?.play();
	}

	return (
		<div className="panel teal-green audio-video-check">
			<p className="header">Check that your sound and video settings are correct</p>
			<div>
				<h3><Icon name={ICONS.SOUND} color={COLORS.DEFAULT_GRAY} size={16}></Icon> <span className="header-text">Sound</span></h3>
				<div className="option-row">
					<SelectSmall
						options={audioDevices.map((dev: MediaDeviceInfo) => ({ label: dev.label, value: dev.deviceId, className: 'truncate-overflow' }))}
						onChange={selectMic}
						label={'Microphone'}
						selected={audioDevices.length > 0 ? { label: selectedAudio?.label || '', value: selectedAudio?.deviceId } : { label: 'Cannot access microphone', value: '' }}
						dropdown={true}
						showLabel={true}
						invalid={!(audioDevices.length > 0)}
						disabled={!(audioDevices.length > 0)}
						optionHeight={46}
					/>
					<div className={classNames('option-row-buttons', audioDevices.length === 0 && 'disabled')}>
						<Icon name={ICONS.MICROPHONE_ON} size={21} color={COLORS.WHITE}></Icon>
						<AudioMeter
							inputStream={mainVideoStream as MediaStream}
							color={Colors.paleGreen}
						/>
					</div>
				</div>
				{	// This check can be removed when Mozilla changes browser setting
					// media.setsinkid.enabled to default to true.  For now, we will not
					// show media select dropdown on firefox since it would be missleading
					(!isFirefox || (isFirefox && audioOutputs.length > 0)) && (
						<div className="option-row">
							<SelectSmall
								options={audioOutputs.map((dev: MediaDeviceInfo) => ({ label: dev.label, value: dev.deviceId, className: 'truncate-overflow' }))}
								onChange={selectSpeaker}
								label={'Speakers'}
								selected={audioOutputs.length > 0 ? { label: selectedOutput?.label || '', value: selectedOutput?.deviceId } : { label: 'Cannot access speakers', value: '' }}
								dropdown={true}
								showLabel={true}
								invalid={!(audioOutputs.length > 0)}
								disabled={!(audioOutputs.length > 0)}
								optionHeight={46}
							/>
							<div className={classNames('option-row-buttons', audioOutputs.length === 0 && 'disabled')}>
								<button className="xsmall" onClick={testAudio} style={{ display: 'flex' }}>
									<Icon name={ICONS.SOUND} size={21} color={COLORS.GRAY_PRESENTER} />
									<span style={{ marginLeft: 5 }}>Test</span>
								</button>
							</div>
						</div>
					)
				}
			</div>
			<div className="video-section">
				<h3><Icon name={ICONS.CAMERA_ON} size={16} color={COLORS.DEFAULT_GRAY}></Icon> <span className="header-text">Video</span></h3>
				<div className="option-row">
					<SelectSmall
						options={videoDevices.map((dev: MediaDeviceInfo) => ({ label: dev.label, value: dev.deviceId, className: 'truncate-overflow' }))}
						onChange={selectCamera}
						label={'Camera'}
						selected={videoDevices.length > 0 ? { label: selectedVideo?.label || '', value: selectedVideo?.deviceId } : { label: 'Cannot access camera', value: '' }}
						dropdown={true}
						showLabel={true}
						invalid={!(videoDevices.length > 0)}
						disabled={!(videoDevices.length > 0)}
						forceOpenUp
						optionHeight={46}
					/>
				</div>
			</div>
			<audio ref={audioEl} src={audioTest}></audio>
		</div>
	);
}
